import { useState } from 'react';
import { IMPRESSION_TYPE } from './useRetainerRequestForm';
import { ENABLE_OCC_BITE_RAMP_QUESTION } from '../../DiffRetainer/utils';

/**
 * @typedef {Object} AllTeethSelections
 * @property {Object} baseTeethSelections - Represents a file case impression type.
 * @property {[number]} baseTeethSelections.toothForBiteRamp - Represents a courier case impression type.
 * @property {[number]} baseTeethSelections.toothForPontics - Represents a scanner case impression type.
 * @property {Object} upperAndLowerTeethSelections - Represents a file case impression type.
 * @property {[number]} upperAndLowerTeethSelections.upperToothForBiteRamp - Represents a courier case impression type.
 * @property {[number]} upperAndLowerTeethSelections.upperToothForPontics - Represents a scanner case impression type.
 * @property {[number]} upperAndLowerTeethSelections.lowerToothForBiteRamp - Represents a courier case impression type.
 * @property {[number]} upperAndLowerTeethSelections.lowerToothForPontics - Represents a scanner case impression type.
 */

const useTeethSelections = () => {
    // for base component
    const [toothForBiteRamp, setToothForBiteRamp] = useState([]);
    const [toothForPontics, setToothForPontics] = useState([]);

    // for upper and lower component
    const [upperToothForBiteRamp, setUpperToothForBiteRamp] = useState([]);
    const [upperToothForPontics, setUpperToothForPontics] = useState([]);
    const [lowerToothForBiteRamp, setLowerToothForBiteRamp] = useState([]);
    const [lowerToothForPontics, setLowerToothForPontics] = useState([]);

    const baseTeethSelections = {
        toothForBiteRamp,
        setToothForBiteRamp,
        toothForPontics,
        setToothForPontics,
    };

    const upperAndLowerTeethSelections = {
        upperToothForBiteRamp,
        setUpperToothForBiteRamp,
        upperToothForPontics,
        setUpperToothForPontics,
        lowerToothForBiteRamp,
        setLowerToothForBiteRamp,
        lowerToothForPontics,
        setLowerToothForPontics,
    };

    /**
     * function to assign teeth selections to form values based on impression type
     * if no teeth selections are made, return the form values as is
     * @type {(formValues:any ,allTeethSelections:AllTeethSelections, impressionType:IMPRESSION_TYPE ) => formValues:any}
     */
    const handleFormValuesWithTeethSelections = (formValues, allTeethSelections, impressionType) => {
        if (!formValues) throw new Error('formValues is required');
        if (!allTeethSelections) throw new Error('allTeethSelections is required');
        if (!impressionType) throw new Error('impressionType is required');
        const cloneFormValues = { ...formValues };
        const { baseTeethSelections } = allTeethSelections;
        const { toothForBiteRamp, toothForPontics } = baseTeethSelections;
        switch (impressionType) {
            case IMPRESSION_TYPE.UPPER:
                if (!toothForBiteRamp.length && !toothForPontics.length) return cloneFormValues;
                if (!ENABLE_OCC_BITE_RAMP_QUESTION.includes(cloneFormValues.archAndQuantity)) {
                    cloneFormValues.biteRamp = null;
                    return cloneFormValues;
                }
                // cloneFormValues.pontics = toothForPontics.length > 0 ?  [...toothForPontics] : null
                cloneFormValues.biteRamp = toothForBiteRamp.length > 0 ? [...toothForBiteRamp] : null;
                return cloneFormValues;
            case IMPRESSION_TYPE.LOWER:
                if (!toothForBiteRamp.length && !toothForPontics.length) return cloneFormValues;
                if (!ENABLE_OCC_BITE_RAMP_QUESTION.includes(cloneFormValues.archAndQuantity)) {
                    cloneFormValues.biteRamp = null;
                    return cloneFormValues;
                }
                // cloneFormValues.pontics = toothForPontics.length > 0 ?  [...toothForPontics] : null
                cloneFormValues.biteRamp = toothForBiteRamp.length > 0 ? [...toothForBiteRamp] : null;
                return cloneFormValues;
            case IMPRESSION_TYPE.UPPER_AND_LOWER:
                const { upperAndLowerTeethSelections } = allTeethSelections;
                const { upperToothForBiteRamp, upperToothForPontics, lowerToothForBiteRamp, lowerToothForPontics } =
                    upperAndLowerTeethSelections;
                if (
                    !upperToothForBiteRamp.length &&
                    !upperToothForPontics.length &&
                    !lowerToothForBiteRamp.length &&
                    !lowerToothForPontics.length
                )
                    return cloneFormValues;
                // cloneFormValues.upperPontics = upperToothForPontics.length > 0 ?  [...upperToothForPontics] : null
                cloneFormValues.upperBiteRamp = upperToothForBiteRamp.length > 0 ? [...upperToothForBiteRamp] : null;
                // cloneFormValues.lowerPontics = lowerToothForPontics.length > 0 ?  [...lowerToothForPontics] : null
                cloneFormValues.lowerBiteRamp = lowerToothForBiteRamp.length > 0 ? [...lowerToothForBiteRamp] : null;
                if (!ENABLE_OCC_BITE_RAMP_QUESTION.includes(cloneFormValues[`UpperArchAndQuantity`])) {
                    cloneFormValues.upperBiteRamp = null;
                }
                if (!ENABLE_OCC_BITE_RAMP_QUESTION.includes(cloneFormValues[`LowerArchAndQuantity`])) {
                    cloneFormValues.lowerBiteRamp = null;
                }
                return cloneFormValues;
            default:
                return cloneFormValues;
        }
    };

    const resetAllTeethSelections = () => {
        setToothForBiteRamp([]);
        setToothForPontics([]);
        setUpperToothForBiteRamp([]);
        setUpperToothForPontics([]);
        setLowerToothForBiteRamp([]);
    };

    return {
        baseTeethSelections,
        upperAndLowerTeethSelections,
        handleFormValuesWithTeethSelections,
        resetAllTeethSelections,
    };
};

export default useTeethSelections;
